import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";

import PublicLayout from "@/components/public/PublicLayout";
import PublicHome from "@/pages/public/PublicHome";
import ServicesIndex from "@/pages/public/ServicesIndex";
import ServiceDetail from "@/pages/public/ServiceDetail";
import WorkplaceChaplaincyPage from "@/pages/public/WorkplaceChaplaincyPage";
import HealthcarePage from "@/pages/public/HealthcarePage";
import LeadershipEmployeeSupportPage from "@/pages/public/LeadershipEmployeeSupportPage";
import TrainingWorkshopsPage from "@/pages/public/TrainingWorkshopsPage";
import IndustryLandingPage from "@/pages/public/IndustryLandingPage";
import BooksIndex from "@/pages/public/BooksIndex";
import BookDetail from "@/pages/public/BookDetail";
import AboutPage from "@/pages/public/AboutPage";
import ForOrganizationsPage from "@/pages/public/ForOrganizationsPage";
import IndividualsFamiliesPage from "@/pages/public/IndividualsFamiliesPage";
import ContactPage from "@/pages/public/ContactPage";
import SchedulePage from "@/pages/public/SchedulePage";
import BulkOrderPage from "@/pages/public/BulkOrderPage";
import PublicResources from "@/pages/public/PublicResources";
import ArticlesIndex from "@/pages/public/ArticlesIndex";
import ArticleDetail from "@/pages/public/ArticleDetail";
import ChaplaincyVsEap from "@/pages/public/ChaplaincyVsEap";
import Packages from "@/pages/public/Packages";
import PublicCareers from "@/pages/public/PublicCareers";
import CareerDetail from "@/pages/public/CareerDetail";
import CareerApply from "@/pages/public/CareerApply";
import GeneralApply from "@/pages/public/GeneralApply";
import AcademyLanding from "@/pages/public/AcademyLanding";
import AcademyApply from "@/pages/public/AcademyApply";
import AcademyHospiceChaplaincy from "@/pages/public/AcademyHospiceChaplaincy";
import AcademyPatientsVoiceInCare from "@/pages/public/AcademyPatientsVoiceInCare";
import AcademyPvcEnroll from "@/pages/public/AcademyPvcEnroll";
import AcademyFd2gFacLanding from "@/pages/public/AcademyFd2gFacLanding";
import AcademyFd2gFacEnroll from "@/pages/public/AcademyFd2gFacEnroll";
import AcademyFd2gFacVerify from "@/pages/public/AcademyFd2gFacVerify";
import CallAChapPage from "@/pages/public/CallAChapPage";
import BalmEmployeeDemo from "@/pages/public/callachap/BalmEmployeeDemo";
import EmployerDashboardDemo from "@/pages/public/callachap/EmployerDashboardDemo";
import PortalLanding from "@/pages/academy-portal/PortalLanding";
import PortalLandingHosp001 from "@/pages/academy-portal/PortalLandingHosp001";
import PortalHospLearn from "@/pages/academy-portal/PortalHospLearn";
import PortalHosp101Learn from "@/pages/academy-portal/PortalHosp101Learn";
import PortalHosp101Certificate from "@/pages/academy-portal/PortalHosp101Certificate";
import PortalFd2gFacLearn from "@/pages/academy-portal/PortalFd2gFacLearn";
import PortalFd2gFacResourceCenter from "@/pages/academy-portal/PortalFd2gFacResourceCenter";
import PortalLandingPvc001 from "@/pages/academy-portal/PortalLandingPvc001";
import PortalPvcLearn from "@/pages/academy-portal/PortalPvcLearn";
import PortalPvcCertificatePreview from "@/pages/academy-portal/PortalPvcCertificatePreview";
import { PortalRegister, PortalLogin } from "@/pages/academy-portal/PortalAuth";
import PortalDashboard from "@/pages/academy-portal/PortalDashboard";
import PortalLearn from "@/pages/academy-portal/PortalLearn";
import PortalCertificate from "@/pages/academy-portal/PortalCertificate";
import { PortalPrivacy, PortalTerms, PortalAccessibility } from "@/pages/academy-portal/PortalLegal";
import LegacyRedirect from "@/components/public/LegacyRedirect";

import Layout from "@/components/site/Layout";
import ProtectedRoute from "@/components/site/ProtectedRoute";
import ScrollToTop from "@/components/site/ScrollToTop";
import HomePage from "@/pages/HomePage";
import WelcomeToChaplainCare from "@/pages/WelcomeToChaplainCare";
import RequestSupportPage from "@/pages/RequestSupportPage";
import ResourcesPage from "@/pages/ResourcesPage";
import OnDemandPage from "@/pages/OnDemandPage";
import CallAChapCarePortalPage from "@/pages/CallAChapCarePortalPage";
import CallAChapOrgPage from "@/pages/CallAChapOrgPage";
import ResourceDetailPage from "@/pages/ResourceDetailPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";

// Care Portal — Demonstration Environments (isolated, no DB, no writes)
import CareDemoHub from "@/pages/care-portal/demo/CareDemoHub";
import BalmDemoHub from "@/pages/care-portal/demo/BalmDemoHub";
import CareBalmEmployee from "@/pages/care-portal/demo/CareBalmEmployee";
import CareBalmEmployer from "@/pages/care-portal/demo/CareBalmEmployer";
import CompanyADemoHub from "@/pages/care-portal/demo/CompanyADemoHub";
import CompanyAEmployeeDemo from "@/pages/care-portal/demo/CompanyAEmployeeDemo";
import CompanyAEmployerDemo from "@/pages/care-portal/demo/CompanyAEmployerDemo";
import AdminDemoSubmissions from "@/pages/admin/AdminDemoSubmissions";
import AdminDemoSettings from "@/pages/admin/AdminDemoSettings";
import AdminActivityReports from "@/pages/admin/AdminActivityReports";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminEmployerUtilization from "@/pages/admin/AdminEmployerUtilization";
import AdminTeam from "@/pages/admin/AdminTeam";
import { AcceptInvitePage, ResetPasswordPage } from "@/pages/admin/AdminTokenPage";
import AdminChangePasswordPage from "@/pages/admin/AdminChangePasswordPage";
import AdminForgotPasswordPage from "@/pages/admin/AdminForgotPasswordPage";
import NotFoundPage from "@/pages/NotFoundPage";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCareCoordination from "@/pages/admin/AdminCareCoordination";
import AdminMyAssignments from "@/pages/admin/AdminMyAssignments";
import ChaplainPortalLoginPage from "@/pages/chaplain-portal/ChaplainPortalLoginPage";
import ChaplainAcceptInvitePage from "@/pages/chaplain-portal/ChaplainAcceptInvitePage";
import ChaplainPortalLayout from "@/components/chaplain-portal/ChaplainPortalLayout";
import ChaplainOnlyRoute from "@/components/chaplain-portal/ChaplainOnlyRoute";
import ChaplainDashboard from "@/pages/chaplain-portal/ChaplainDashboard";
import ChaplainCareDetail from "@/pages/chaplain-portal/ChaplainCareDetail";
import ChaplainHistory from "@/pages/chaplain-portal/ChaplainHistory";
import ChaplainProfile from "@/pages/chaplain-portal/ChaplainProfile";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminGriefAppointments from "@/pages/admin/AdminGriefAppointments";
import AdminBulkOrders from "@/pages/admin/AdminBulkOrders";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminJobEdit from "@/pages/admin/AdminJobEdit";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminAcademyApplicants from "@/pages/admin/AdminAcademyApplicants";
import AdminAcademyPortal from "@/pages/admin/AdminAcademyPortal";
import FAQPage from "@/pages/public/FAQPage";

const publicPages = [
  { path: "/", el: <PublicHome /> },
  { path: "/services", el: <ServicesIndex /> },
  { path: "/services/workplace-chaplaincy", el: <WorkplaceChaplaincyPage /> },
  { path: "/services/healthcare-hospice", el: <HealthcarePage /> },
  { path: "/services/leadership-employee-support", el: <LeadershipEmployeeSupportPage /> },
  { path: "/services/training-workshops", el: <TrainingWorkshopsPage /> },
  { path: "/services/:slug", el: <ServiceDetail /> },
  { path: "/industries/:slug", el: <IndustryLandingPage /> },
  { path: "/books", el: <BooksIndex /> },
  { path: "/books/:slug", el: <BookDetail /> },
  { path: "/about", el: <AboutPage /> },
  { path: "/for-organizations", el: <ForOrganizationsPage /> },
  { path: "/individuals-families", el: <IndividualsFamiliesPage /> },
  { path: "/contact", el: <ContactPage /> },
  { path: "/schedule", el: <SchedulePage /> },
  { path: "/bulk-order", el: <BulkOrderPage /> },
  { path: "/resources", el: <PublicResources /> },
  { path: "/resources/articles", el: <ArticlesIndex /> },
  { path: "/resources/articles/:slug", el: <ArticleDetail /> },
  { path: "/resources/chaplaincy-vs-eap", el: <ChaplaincyVsEap /> },
  { path: "/packages", el: <Packages /> },
  { path: "/careers", el: <PublicCareers /> },
  { path: "/careers/apply", el: <GeneralApply /> },
  { path: "/careers/:slug", el: <CareerDetail /> },
  { path: "/careers/:slug/apply", el: <CareerApply /> },
  { path: "/academy", el: <AcademyLanding /> },
  { path: "/academy/hospice-chaplaincy", el: <AcademyHospiceChaplaincy /> },
  { path: "/academy/patients-voice-in-care", el: <AcademyPatientsVoiceInCare /> },
  { path: "/academy/pvc-001/enroll", el: <AcademyPvcEnroll /> },
  { path: "/academy/fd2g-fac-001", el: <AcademyFd2gFacLanding /> },
  { path: "/academy/fd2g-fac-001/enroll", el: <AcademyFd2gFacEnroll /> },
  { path: "/academy/fd2g-fac-001/verify", el: <AcademyFd2gFacVerify /> },
  { path: "/academy/fd2g-fac-001/verify/:credential_id", el: <AcademyFd2gFacVerify /> },
  { path: "/academy/apply", el: <AcademyApply /> },
  { path: "/callachap", el: <CallAChapPage /> },
  { path: "/faq", el: <FAQPage /> },
  { path: "/callachap/demo/balm", el: <BalmEmployeeDemo /> },
  { path: "/callachap/demo/employer", el: <EmployerDashboardDemo /> },
];

// Portal routes (own layout — no PublicLayout wrapping, no chatbox)
const portalRoutes = [
  { path: "/academy/portal", el: <PortalLanding /> },
  { path: "/academy/portal/hosp-001", el: <PortalLandingHosp001 /> },
  { path: "/academy/portal/hosp-001/learn", el: <PortalHospLearn /> },
  { path: "/academy/portal/hosp-101/learn", el: <PortalHosp101Learn /> },
  { path: "/academy/portal/hosp-101/certificate", el: <PortalHosp101Certificate /> },
  { path: "/academy/portal/hosp-101/certificate-preview", el: <PortalHosp101Certificate /> },
  { path: "/academy/portal/fd2g-fac-001/learn", el: <PortalFd2gFacLearn /> },
  { path: "/academy/portal/fd2g-fac-001/resource-center", el: <PortalFd2gFacResourceCenter /> },
  { path: "/academy/portal/pvc-001", el: <PortalLandingPvc001 /> },
  { path: "/academy/portal/pvc-001/learn", el: <PortalPvcLearn /> },
  { path: "/academy/portal/pvc-001/certificate-preview", el: <PortalPvcCertificatePreview /> },
  { path: "/academy/portal/register", el: <PortalRegister /> },
  { path: "/academy/portal/login", el: <PortalLogin /> },
  { path: "/academy/portal/dashboard", el: <PortalDashboard /> },
  { path: "/academy/portal/learn/:sectionId", el: <PortalLearn /> },
  { path: "/academy/portal/certificate/:certId", el: <PortalCertificate /> },
  { path: "/academy/portal/privacy", el: <PortalPrivacy /> },
  { path: "/academy/portal/terms", el: <PortalTerms /> },
  { path: "/academy/portal/accessibility", el: <PortalAccessibility /> },
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <Routes>
            {publicPages.map((p) => (<Route key={p.path} path={p.path} element={<PublicLayout>{p.el}</PublicLayout>} />))}

            {/* Academy Portal — own shell, no chatbox */}
            {portalRoutes.map((p) => (<Route key={p.path} path={p.path} element={p.el} />))}

            <Route path="/care-portal" element={<Layout><HomePage /></Layout>} />
            <Route path="/care-portal/start-here" element={<Layout><WelcomeToChaplainCare /></Layout>} />
            <Route path="/care-portal/request-support" element={<Layout><RequestSupportPage /></Layout>} />
            <Route path="/care-portal/resources" element={<Layout><ResourcesPage /></Layout>} />
            <Route path="/care-portal/on-demand" element={<Layout><OnDemandPage /></Layout>} />
            <Route path="/care-portal/callachap" element={<Layout><CallAChapCarePortalPage /></Layout>} />
            <Route path="/care-portal/callachap/:orgSlug" element={<Layout><CallAChapOrgPage /></Layout>} />
            <Route path="/care-portal/resources/:slug" element={<Layout><ResourceDetailPage /></Layout>} />

            {/* Care Portal — Demonstration Environments (public, isolated, no DB writes) */}
            <Route path="/care-portal/demo" element={<Layout><CareDemoHub /></Layout>} />
            <Route path="/care-portal/demo/balm" element={<Layout><BalmDemoHub /></Layout>} />
            <Route path="/care-portal/demo/balm/employee" element={<Layout><CareBalmEmployee /></Layout>} />
            <Route path="/care-portal/demo/balm/employer" element={<Layout><CareBalmEmployer /></Layout>} />
            <Route path="/care-portal/demo/company-a" element={<Layout><CompanyADemoHub /></Layout>} />
            <Route path="/care-portal/demo/company-a/employee" element={<Layout><CompanyAEmployeeDemo /></Layout>} />
            <Route path="/care-portal/demo/company-a/employer" element={<Layout><CompanyAEmployerDemo /></Layout>} />

            <Route path="/care-portal/admin/login" element={<Layout><AdminLoginPage /></Layout>} />
            <Route path="/care-portal/admin/accept-invite" element={<Layout><AcceptInvitePage /></Layout>} />
            <Route path="/care-portal/admin/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
            <Route path="/care-portal/admin/forgot-password" element={<Layout><AdminForgotPasswordPage /></Layout>} />
            <Route path="/care-portal/admin/change-password" element={<Layout><AdminChangePasswordPage /></Layout>} />

            {/* Admin management center — nested under AdminLayout */}
            <Route path="/care-portal/admin" element={<ProtectedRoute><Layout><AdminLayout /></Layout></ProtectedRoute>}>
              <Route path="dashboard" element={<AdminOverview />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="care-coordination" element={<AdminCareCoordination />} />
              <Route path="my-assignments" element={<AdminMyAssignments />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="grief-appointments" element={<AdminGriefAppointments />} />
              <Route path="bulk-orders" element={<AdminBulkOrders />} />
              <Route path="inbox" element={<AdminInbox />} />
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="jobs/new" element={<AdminJobEdit />} />
              <Route path="jobs/:id/edit" element={<AdminJobEdit />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="academy-applicants" element={<AdminAcademyApplicants />} />
              <Route path="academy-portal" element={<AdminAcademyPortal />} />
              <Route path="support-requests" element={<AdminDashboardPage />} />
              <Route path="activity-reports" element={<AdminActivityReports />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="employer-utilization" element={<AdminEmployerUtilization />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="demo-submissions" element={<AdminDemoSubmissions />} />
              <Route path="demo-settings" element={<AdminDemoSettings />} />
            </Route>

            {/* Chaplain Portal — separate staff surface for role=chaplain */}
            <Route path="/chaplain-portal/login" element={<ChaplainPortalLoginPage />} />
            <Route path="/chaplain-portal/accept-invite" element={<ChaplainAcceptInvitePage />} />
            <Route
              path="/chaplain-portal"
              element={<ChaplainOnlyRoute><ChaplainPortalLayout /></ChaplainOnlyRoute>}
            >
              <Route index element={<Navigate to="/chaplain-portal/dashboard" replace />} />
              <Route path="dashboard" element={<ChaplainDashboard />} />
              <Route path="follow-up" element={<ChaplainDashboard filter="follow_up" />} />
              <Route path="completed" element={<ChaplainDashboard filter="completed" />} />
              <Route path="care/:id" element={<ChaplainCareDetail />} />
              <Route path="history" element={<ChaplainHistory />} />
              <Route path="profile" element={<ChaplainProfile />} />
            </Route>

            {/* Legacy Care Portal URLs */}
            <Route path="/request-support" element={<LegacyRedirect to="/care-portal/request-support" />} />
            <Route path="/admin/login" element={<LegacyRedirect to="/care-portal/admin/login" />} />
            <Route path="/admin/dashboard" element={<LegacyRedirect to="/care-portal/admin/dashboard" />} />

            {/* 404 — pathname-aware, wraps in Layout for /care-portal/* so the site chrome stays */}
            <Route path="/care-portal/*" element={<Layout><NotFoundPage /></Layout>} />
            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
